import { Component, type ErrorInfo, type ReactNode, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RotateCcw, RefreshCw } from "lucide-react";
import { ReportErrorDialog, type ReportErrorContext } from "@/components/support/ReportErrorDialog";
import { attemptChunkReload, isChunkLoadError } from "@/lib/chunk-reload";

interface State {
  error: Error | null;
  componentStack: string | null;
  staleVersion: boolean;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null, componentStack: null, staleVersion: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Auto-recover from stale chunk references after a deploy.
    if (isChunkLoadError(error)) {
      if (attemptChunkReload()) return;
      this.setState({ error, componentStack: info.componentStack ?? null, staleVersion: true });
      return;
    }
    this.setState({ error, componentStack: info.componentStack ?? null, staleVersion: false });
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, info);
  }

  reset = () => this.setState({ error: null, componentStack: null, staleVersion: false });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.state.staleVersion) return <StaleVersionFallback />;
    return (
      <ErrorFallback
        error={this.state.error}
        componentStack={this.state.componentStack}
        onReset={this.reset}
      />
    );
  }
}

function StaleVersionFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <RefreshCw className="h-6 w-6 text-primary" />
        </div>
        <h1 className="text-2xl font-semibold">A new version of Vitolier is available</h1>
        <p className="text-sm text-muted-foreground">
          Reload the page to continue with the latest version.
        </p>
        <div className="flex justify-center pt-2">
          <Button onClick={() => window.location.reload()}>
            <RotateCcw className="mr-2 h-4 w-4" /> Reload
          </Button>
        </div>
      </div>
    </div>
  );
}


function ErrorFallback({
  error,
  componentStack,
  onReset,
}: {
  error: Error;
  componentStack: string | null;
  onReset: () => void;
}) {
  const [reportOpen, setReportOpen] = useState(false);
  const context: ReportErrorContext = {
    error,
    componentStack: componentStack ?? undefined,
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="text-sm text-muted-foreground">
          {error.message || "An unexpected error occurred."}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
          <Button onClick={onReset} variant="outline">
            <RotateCcw className="mr-2 h-4 w-4" /> Try again
          </Button>
          <Button onClick={() => setReportOpen(true)}>Report to Support</Button>
        </div>
        <ReportErrorDialog open={reportOpen} onOpenChange={setReportOpen} context={context} />
      </div>
    </div>
  );
}

/**
 * Mount once in App root. Listens for `vitolier:open-error-report` events
 * (dispatched by sonner toast actions or anywhere else) and opens the dialog.
 */
export function GlobalErrorReportListener() {
  const [ctx, setCtx] = useState<ReportErrorContext | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ReportErrorContext | undefined;
      setCtx(detail ?? { errorMessage: "Unknown error" });
    };
    window.addEventListener("vitolier:open-error-report", handler as EventListener);
    return () => window.removeEventListener("vitolier:open-error-report", handler as EventListener);
  }, []);
  if (!ctx) return null;
  return (
    <ReportErrorDialog
      open={!!ctx}
      onOpenChange={(o) => !o && setCtx(null)}
      context={ctx}
    />
  );
}
